import React, { useState, useEffect } from 'react';
import { X, Tag, Check, AlertCircle } from 'lucide-react';
import { CartContextType, Order, PromoCode, UserProfile } from '../types';

interface CheckoutModalProps {
  cart: CartContextType;
  onClose: () => void;
  onPlaceOrder: (orderData: Omit<Order, 'id' | 'status' | 'date'>) => void;
  promoCodes?: PromoCode[];
  currentUser: UserProfile | null;
}

export const CheckoutModal: React.FC<CheckoutModalProps> = ({ cart, onClose, onPlaceOrder, promoCodes = [], currentUser }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    paymentMethod: 'COD' as const
  });

  // Auto-fill user data
  useEffect(() => {
    if (currentUser) {
      setFormData(prev => ({
        ...prev,
        name: currentUser.username || '',
        email: currentUser.email || '',
        phone: currentUser.phone || ''
      }));
    }
  }, [currentUser]);

  // Promo Code State
  const [promoCodeInput, setPromoCodeInput] = useState('');
  const [appliedPromo, setAppliedPromo] = useState<PromoCode | null>(null);
  const [promoError, setPromoError] = useState('');

  const calculateTotal = () => {
    let total = cart.cartTotal;
    if (appliedPromo) {
        if (appliedPromo.type === 'percentage') {
            total = total - (total * (appliedPromo.value / 100));
        } else {
            total = total - appliedPromo.value;
        }
    }
    return Math.max(0, total);
  };

  const handleApplyPromo = () => {
    setPromoError('');
    setAppliedPromo(null);
    
    if (!promoCodeInput.trim()) return;

    const foundPromo = promoCodes.find(p => p.code.toUpperCase() === promoCodeInput.trim().toUpperCase() && p.isActive);
    
    if (!foundPromo) {
        setPromoError('Invalid coupon code');
        return;
    }

    if (cart.cartTotal < foundPromo.minOrderAmount) {
        setPromoError(`Minimum order of Rs. ${foundPromo.minOrderAmount} required`);
        return;
    }

    setAppliedPromo(foundPromo);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const finalTotal = calculateTotal();
    const discount = cart.cartTotal - finalTotal;

    onPlaceOrder({
      customerName: formData.name,
      customerPhone: formData.phone,
      customerEmail: formData.email,
      address: formData.address,
      items: cart.items,
      subtotal: cart.cartTotal,
      discount: discount,
      total: finalTotal,
      promoCode: appliedPromo ? appliedPromo.code : undefined,
      paymentMethod: formData.paymentMethod
    });
  };

  return (
    <div className="fixed inset-0 z-[200] bg-black bg-opacity-75 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-lg w-full p-6 animate-slide-in-left max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Checkout</h2>
          <button onClick={onClose}><X size={24} /></button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Full Name</label>
            <input required type="text" className="mt-1 w-full border rounded-lg p-3"
              value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
             <div>
                <label className="block text-sm font-medium text-gray-700">Phone Number (+92...)</label>
                <input required type="tel" className="mt-1 w-full border rounded-lg p-3"
                  value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} />
             </div>
             <div>
                <label className="block text-sm font-medium text-gray-700">Email Address</label>
                <input required type="email" className="mt-1 w-full border rounded-lg p-3"
                  value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
             </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Delivery Address</label>
            <textarea required rows={3} className="mt-1 w-full border rounded-lg p-3"
              value={formData.address} onChange={e => setFormData({...formData, address: e.target.value})} />
          </div>

          <div>
             <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method</label>
             <div className="grid grid-cols-3 gap-2">
                {['COD', 'JazzCash', 'EasyPaisa'].map(method => (
                    <button
                        key={method}
                        type="button"
                        onClick={() => setFormData({...formData, paymentMethod: method as any})}
                        className={`py-2 text-sm border rounded-lg ${
                            formData.paymentMethod === method 
                            ? 'bg-blue-600 text-white border-blue-600' 
                            : 'bg-white text-gray-700 border-gray-300'
                        }`}
                    >
                        {method}
                        {method === 'COD' && <span className="block text-[10px] opacity-75">Cash on Delivery</span>}
                    </button>
                ))}
             </div>
          </div>

          {/* Promo Code Section */}
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
              <label className="block text-xs font-bold uppercase text-gray-500 mb-2 flex items-center gap-1"><Tag size={14}/> Have a Promo Code?</label>
              <div className="flex gap-2">
                  <input 
                    type="text" 
                    className="flex-1 border rounded-lg p-2 text-sm uppercase" 
                    placeholder="Enter Code"
                    value={promoCodeInput}
                    onChange={e => setPromoCodeInput(e.target.value)}
                    disabled={!!appliedPromo}
                  />
                  {appliedPromo ? (
                      <button type="button" onClick={() => { setAppliedPromo(null); setPromoCodeInput(''); }} className="text-red-500 font-bold text-xs">Remove</button>
                  ) : (
                      <button type="button" onClick={handleApplyPromo} className="bg-black text-white px-4 rounded-lg text-sm font-bold">Apply</button>
                  )}
              </div>
              {promoError && <p className="text-red-500 text-xs mt-1 flex items-center gap-1"><AlertCircle size={12}/> {promoError}</p>}
              {appliedPromo && (
                  <p className="text-green-600 text-xs mt-1 flex items-center gap-1">
                      <Check size={12}/> Code <b>{appliedPromo.code}</b> applied! 
                      ({appliedPromo.type==='percentage' ? `${appliedPromo.value}% OFF` : `Rs.${appliedPromo.value} OFF`})
                  </p>
              )}
          </div>

          <div className="border-t pt-4 mt-4 space-y-2">
             <div className="flex justify-between text-gray-500">
                <span>Subtotal</span>
                <span>Rs. {cart.cartTotal.toLocaleString()}</span>
             </div>
             {appliedPromo && (
                 <div className="flex justify-between text-green-600 font-medium">
                    <span>Discount</span>
                    <span>- Rs. {(cart.cartTotal - calculateTotal()).toLocaleString()}</span>
                 </div>
             )}
             <div className="flex justify-between font-bold text-xl pt-2 border-t">
                <span>Total Amount</span>
                <span>Rs. {calculateTotal().toLocaleString()}</span>
             </div>
             <button type="submit" className="w-full bg-blue-600 text-white py-4 rounded-xl font-bold hover:bg-blue-700 shadow-lg mt-4">
                Place Order
             </button>
          </div>
        </form>
      </div>
    </div>
  );
};